import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminChanges({ token }) {
  const [changes, setChanges] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/changes${filter ? `?status=${filter}` : ""}`, { headers: { "X-Admin-Token": token } });
      const data = await res.json();
      setChanges(data.changes || []);
    } catch {
      toast.error("Could not load requests");
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    setBusy(id + action);
    const res = await fetch(`${API}/admin/changes/${id}/resolve`, {
      method: "POST",
      headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
      body: JSON.stringify({ action, note: "" }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy("");
    if (!res.ok) { toast.error(data.detail || "Failed"); return; }
    toast.success(action === "approve" ? "Approved — ticket updated" : "Request rejected");
    load();
  };

  return (
    <div className="mt-2" data-testid="admin-changes">
      <div className="flex flex-wrap gap-px bg-[#2e2e2a] border border-[#2e2e2a] w-fit max-w-full" data-testid="changes-filter">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`changes-filter-${s || "all"}`}
            className={`px-4 py-2.5 min-h-[44px] font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${filter === s ? "bg-[#ff5a1f] text-[#0c0c0a]" : "bg-[#161614] text-[#a3a39f] hover:text-[#f0eee4]"}`}
          >
            {s || "ALL"}
          </button>
        ))}
      </div>

      {!changes ? (
        <p className="mt-10 font-mono text-[11px] tracking-[0.2em] text-[#6e6e6a]">LOADING…</p>
      ) : changes.length === 0 ? (
        <p className="mt-10 font-mono text-[11px] tracking-[0.2em] text-[#6e6e6a]" data-testid="changes-empty">NO {filter ? filter.toUpperCase() : ""} REQUESTS</p>
      ) : (
        <div className="mt-6 space-y-3" data-testid="changes-list">
          {changes.map((c) => (
            <div key={c.id} className="border border-[#2e2e2a] p-5" data-testid={`change-card-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[9px] tracking-[0.2em] border border-[#454540] px-2 py-1 uppercase">
                    {c.type === "name_change" ? "NAME CHANGE" : "TRANSFER"}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.15em] text-[#6e6e6a]">{c.booking_ref}</span>
                </div>
                <span className="font-mono text-[10px] tracking-[0.15em] text-[#6e6e6a]">{fmt(c.requested_at)}</span>
              </div>
              <p className="mt-3 font-display font-bold uppercase text-xl leading-tight">
                {c.current_name} <span className="text-[#6e6e6a]">→</span> {c.new_first_name} {c.new_last_name}
              </p>
              {c.type === "transfer" && (
                <p className="mt-1 font-mono text-[11px] text-[#a3a39f]">{c.new_email} · {c.new_phone}</p>
              )}
              {c.status === "pending" ? (
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => resolve(c.id, "approve")}
                    disabled={!!busy}
                    data-testid={`change-approve-${c.id}`}
                    className="btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:bg-white disabled:opacity-40"
                  >
                    {busy === c.id + "approve" ? "WORKING…" : "APPROVE"}
                  </button>
                  <button
                    onClick={() => resolve(c.id, "reject")}
                    disabled={!!busy}
                    data-testid={`change-reject-${c.id}`}
                    className="border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-5 py-3 min-h-[44px] hover:border-[#f0eee4] transition-colors disabled:opacity-40"
                  >
                    REJECT
                  </button>
                </div>
              ) : (
                <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#a3a39f]">
                  {c.status} · {fmt(c.resolved_at)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
